import React, { useState, useContext, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { ContentCopy } from "@mui/icons-material";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { UserContext } from "../../context/UserContext";
import { useTranslation } from "react-i18next";
import {
	FormControl,
	FormControlLabel,
	InputLabel,
	Radio,
	RadioGroup,
	CardActions,
	Button,
	Alert,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import axios from "axios";
import { ADMIN_URL } from "../../utils/constants";
import { useNavigate } from "react-router-dom";

const useStyles = makeStyles({
	alert: {
		"& .MuiAlert-message": {
			display: "flex",
			justifyContent: "space-between",
			width: "100%",
		},
	},
	pickerContainer: {
		"& #font-picker": {
			width: "100%!important",
			background: "none!important",
			backgroundColor: "none!important",
			boxShadow: "none!important",
			border: "1px solid #D0D0D0",
			borderRadius: 4,
			padding: 4,
		},

		"& #font-picker button": {
			background: "none!important",
		},
		"& #font-picker ul": {
			background: "fafafa!important",
			boxShadow: "none!important",
		},
	},
	logo: {
		height: "100%",
		objectFit: "contain",
		maxWidth: 100,
		margin: "12px 0px",
	},
	formControl: {
		"& hover": {
			backgroundColor: "#434343",
		},
	},
});

export default function IframeWeb({ drawerWidth }) {
	const { t } = useTranslation();
	const { user } = useContext(UserContext);
	const navigate = useNavigate();
	const [generalData, setGeneralData] = useState({
		mainColor: "#43BD98", // Default color
		backgroundColor: "#ffffff", // Default color
		fontColor: "#000000",
		fontFamily: "Montserrat",
		headerColor: "#f8f8f8",
		buttonStyle: "buttonStyle1",
		showShadows: true,
		currency: "eur",
		language: "en",
	});
	// State to handle the current tab
	const [selectedTab, setSelectedTab] = useState(0);

	// Separate state for temporary input values (before validation)
	const [tempWidthValue, setTempWidthValue] = useState("100");
	const [tempHeightValue, setTempHeightValue] = useState("700");

	// Actual validated width and height values used for iframe
	const [widthValue, setWidthValue] = useState("100");
	const [heightValue, setHeightValue] = useState("700");
	const [widthUnit, setWidthUnit] = useState("%");
	const [heightUnit, setHeightUnit] = useState("px");
	const [loading, setLoading] = useState(false);
	const [copied, setCopied] = useState(false);

	const classes = useStyles();

	// Validation for width onBlur
	const validateWidth = () => {
		const numWidth = parseInt(tempWidthValue, 10);
		if (
			widthUnit === "%" &&
			(isNaN(numWidth) || numWidth < 1 || numWidth > 100)
		) {
			setWidthValue(100); // Reset to 100% if out of bounds
			setTempWidthValue(100); // Keep the input field consistent
		} else if (widthUnit === "px" && (isNaN(numWidth) || numWidth < 1)) {
			setWidthValue(1); // Ensure at least 1px
			setTempWidthValue(1);
		} else {
			setWidthValue(tempWidthValue); // Set the validated value
		}
	};

	// Validation for height onBlur
	const validateHeight = () => {
		const numHeight = parseInt(tempHeightValue, 10);
		if (
			heightUnit === "px" &&
			(isNaN(numHeight) || numHeight < 1 || numHeight > 1500)
		) {
			setHeightValue(1500); // Reset to max 1500px if out of bounds
			setTempHeightValue(1500);
		} else if (
			heightUnit === "%" &&
			(isNaN(numHeight) || numHeight < 1 || numHeight > 100)
		) {
			setHeightValue(100); // Reset to 100% for height
			setTempHeightValue(100);
		} else {
			setHeightValue(tempHeightValue); // Set the validated value
		}
	};

	// Dynamically generate the iframe code with updated width and height
	const iframeCode = `<iframe src="${
		process.env.REACT_APP_ENV === "production"
			? "https://book.applaby.com/"
			: "http://localhost:3003/"
	}${
		generalData?.id
	}" style="width: ${widthValue}${widthUnit}; height: ${heightValue}${heightUnit};" frameborder="0"></iframe>`;

	useEffect(() => {
		setLoading(true);
		const timer = setTimeout(() => {
			setLoading(false);
		}, 500);
		return () => clearTimeout(timer);
	}, [widthValue, widthUnit, heightValue, heightUnit]);

	useEffect(() => {
		const getGeneralData = async () => {
			setLoading(true);
			try {
				const res = await axios.get(ADMIN_URL + "creator-iframe", {
					headers: { authorization: localStorage.getItem("token") },
				});
				setGeneralData({
					...res.data,
				});
			} catch (error) {
				console.log(error);
			}
			setLoading(false);
		};

		getGeneralData();
	}, []);

	const saveGeneralData = async () => {
		setLoading(true);
		try {
			await axios.post(
				ADMIN_URL + "creator-iframe",
				{
					...generalData,
				},
				{
					headers: { authorization: localStorage.getItem("token") },
				}
			);
			alert.show(t("general.success"), { type: "success" });
		} catch (error) {
			console.log(error);
		}
		setLoading(false);
	};

	return (
		<>
			<Box
				component="main"
				sx={{
					flexGrow: 1,
					bgcolor: "background.default",
					p: 3,
					width: { xs: "100%", sm: `calc(100vw - ${drawerWidth}px)` },
				}}
			>
				<Toolbar />

				{!user?.app.name && (
					<Alert
						severity="info"
						sx={{
							marginBottom: 2,
							cursor: "pointer",
							"& *": {
								cursor: "pointer",
							},
							"& .MuiAlert-message": {
								display: "flex",
								justifyContent: "space-between",
								width: "100%",
								"&:hover": {
									textDecoration: "underline",
								},
							},
						}}
						onClick={() => {
							navigate("/");
						}}
					>
						{user?.subscription?.isOnlyWeb
							? t("iframe.alertWeb")
							: t("iframe.alert")}
					</Alert>
				)}

				<Grid container spacing={4}>
					<Grid item xs={12} sm={12} md={12} lg={12}>
						<Card variant="outlined" sx={{ marginBottom: 4 }}>
							<CardContent>
								<Typography
									sx={{
										fontSize: 14,
										borderBottom: "1px solid #f0f0f0",
										minHeight: "35px",
										marginLeft: "-20px",
										marginRight: "-20px",
										padding: "0px 20px 20px",
									}}
									color="text.secondary"
									gutterBottom
								>
									{t("iframe.howToUse")}
								</Typography>

								<>
									<Typography mb={2}>{t("iframe.description")}</Typography>
									<Typography sx={{ marginBottom: "10px" }}>
										{t("iframe.step1")}
									</Typography>
									<TextField
										id="domain-input"
										placeholder={t("iframe.domain")}
										variant="outlined"
										fullWidth
										value={generalData.domain}
										sx={{ marginBottom: "20px" }}
										onChange={(e) => {
											setGeneralData({
												...generalData,
												domain: e.target.value,
											});
										}}
									/>

									<Typography sx={{ marginBottom: "10px" }}>
										{t("iframe.extraStep")}
									</Typography>
									<TextField
										id="terms-input"
										placeholder={t("iframe.terms")}
										variant="outlined"
										fullWidth
										value={generalData.termsURL}
										onChange={(e) => {
											setGeneralData({
												...generalData,
												termsURL: e.target.value,
											});
										}}
										sx={{ marginBottom: "20px" }}
									/>
									<TextField
										id="policy-input"
										placeholder={t("iframe.policy")}
										variant="outlined"
										fullWidth
										value={generalData.policyURL}
										sx={{ marginBottom: "20px" }}
										onChange={(e) => {
											setGeneralData({
												...generalData,
												policyURL: e.target.value,
											});
										}}
									/>

									<Typography sx={{ marginBottom: "20px" }}>
										{t("iframe.step2")}
									</Typography>

									{/* Width input and unit selector side by side */}
									<Grid container spacing={2} sx={{ marginBottom: "20px" }}>
										<Grid item xs={8}>
											<TextField
												label={t("iframe.width")}
												variant="outlined"
												fullWidth
												value={tempWidthValue}
												onChange={(e) =>
													setTempWidthValue(e.target.value.replace(/\D/g, ""))
												} // Restrict to numbers
												onBlur={validateWidth} // Validate onBlur
											/>
										</Grid>
										<Grid item xs={4}>
											<TextField
												select
												label={t("iframe.widthUnit")}
												value={widthUnit}
												onChange={(e) => setWidthUnit(e.target.value)}
												fullWidth
											>
												<MenuItem value="%">
													{t("iframe.widthPercentage")}
												</MenuItem>
												<MenuItem value="px">
													{t("iframe.widthPixels")}
												</MenuItem>
											</TextField>
										</Grid>
									</Grid>
									{/* Height input and unit selector side by side */}
									<Grid container spacing={2} sx={{ marginBottom: "20px" }}>
										<Grid item xs={8}>
											<TextField
												label={t("iframe.height")}
												variant="outlined"
												fullWidth
												value={tempHeightValue}
												onChange={(e) =>
													setTempHeightValue(e.target.value.replace(/\D/g, ""))
												} // Restrict to numbers
												onBlur={validateHeight} // Validate onBlur
											/>
										</Grid>
										<Grid item xs={4}>
											<TextField
												select
												label={t("iframe.heightUnit")}
												value={heightUnit}
												onChange={(e) => setHeightUnit(e.target.value)}
												fullWidth
											>
												<MenuItem value="px">
													{t("iframe.heightPixels")}
												</MenuItem>
												<MenuItem value="%">
													{t("iframe.heightPercentage")}
												</MenuItem>
											</TextField>
										</Grid>
									</Grid>

									<Typography sx={{ marginBottom: "20px" }}>
										{t("iframe.step3")}
									</Typography>

									<TextField
										id="iframe-code"
										placeholder={t("iframe.code")}
										variant="outlined"
										fullWidth
										value={iframeCode}
										InputProps={{
											readOnly: true,
											endAdornment: (
												<InputAdornment position="end">
													<CopyToClipboard
														text={iframeCode}
														onCopy={() => {
															setCopied(true);
															setTimeout(() => setCopied(false), 2000);
														}}
													>
														<Tooltip
															title={
																copied ? t("iframe.copied") : t("iframe.copy")
															}
															arrow
														>
															<IconButton>
																<ContentCopy sx={{ fontSize: 18 }} />
															</IconButton>
														</Tooltip>
													</CopyToClipboard>
												</InputAdornment>
											),
										}}
										sx={{ marginBottom: "20px" }}
									/>

									<Typography sx={{ marginBottom: "20px" }}>
										{t("iframe.step4")}
									</Typography>
								</>
							</CardContent>
							<CardActions sx={{ justifyContent: "right" }}>
								<Button
									variant="contained"
									size="small"
									onClick={saveGeneralData}
									disabled={loading}
								>
									{loading ? <CircularProgress size={12} /> : t("general.save")}
								</Button>
							</CardActions>
						</Card>
					</Grid>
				</Grid>
			</Box>
		</>
	);
}
